//* 组件库
import type { UiTypes } from "various-ui";
//* 公共组件
import type { DefaultSearch } from "@/components/controls";
//* 组件属性
import type { ModuleData, ModuleSearchData, ModulePredictionData } from "../index";

export const useComposable = () => {
    //* 基因检索数据
    const data = reactive<ModuleData>({
        search: { id: "", load: false, mode: "Search", name: "", symbol: "", status: "", keyword: "", transcript: "", transcripts: [] },
        prediction: {
            id: "",
            load: false,
            mode: "RNA Splicer",
            name: "",
            symbol: "",
            status: "",
            keyword: "",
            transcript: "",
            transcripts: [],
        },
    });

    //* 节点
    const nodes = {
        ai_input: ref<InstanceType<typeof DefaultSearch>>(),
    };

    //* 函数列表
    const methods = {
        //* 数据重置
        reset: (data: ModuleSearchData | ModulePredictionData, mode?: "change") => {
            data.id = "";
            data.name = "";
            data.symbol = "";
            data.status = "";
            data.keyword = "";
            data.response = undefined;
            data.transcript = "";
            data.transcripts = [];
            nodes.ai_input.value?.reset();
            if (mode != "change" && data.mode != "Search") {
                data.mode = "RNA Splicer";
            }
        },

        //* 基因检索
        change: (data: ModuleSearchData | ModulePredictionData, response?: UiTypes.candidate) => {
            data.load = true;
            api.frontend("/api/gene/mrnas-info", {
                data: { id: response?.id },
                hidden: true,
                finally: () => ((data.load = false), (data.id = response?.id), (data.name = response?.value), (data.symbol = response?.value)),
                success: (response) => {
                    //* 数据更新
                    data.transcripts = response.data || [];
                    data.transcript = data.transcripts?.[0].value;
                    //* 触发转录本切换回调
                    methods.transcript(data);
                },
            });
        },

        //* 突变检索
        search: (data: ModuleSearchData | ModulePredictionData) => {
            const keyword = data.keyword.trim();
            if (!keyword) (data.status = "error"), (data.response = undefined);
            else {
                //* 开启加载器
                data.load = true;
                //* 检测突变信息是否合法
                api.frontend("/api/mutations/analyze2", {
                    data: { id: data.id, keyword, transcript: data.transcript },
                    hidden: true,
                    method: "POST",
                    error: () => ((data.status = "error"), (data.response = undefined)),
                    finally: () => (data.load = false),
                    success: (response) => {
                        if (!response.site) (data.status = "error"), (data.response = undefined);
                        else {
                            data.status = "success";
                            data.response = Object.assign(response, { keyword, name: data.symbol, transcripts: data.transcripts });
                        }
                    },
                });
            }
        },

        //* 数据获取
        obtain: (response: ModulePredictionData) => {
            if (!data.search.response) return;
            else {
                //* 数据更新
                response.id = data.search.response.id;
                response.name = data.search.response.name;
                response.symbol = data.search.response.name;
                response.status = data.search.status;
                response.keyword = data.search.response.keyword;
                response.response = data.search.response;
                response.transcript = data.search.response.transcript;
                response.transcripts = data.search.response.transcripts;
                //* 重置输入框内容
                nodes.ai_input.value?.reset({ keyword: response.keyword });
            }
        },

        //* 切换转录本
        transcript: (data: ModuleSearchData | ModulePredictionData) => {
            data.response = undefined;
            data.keyword = "";
            data.status = "";
        },

        //* 预测接口
        prediction: (data: ModulePredictionData) => {
            if (!data.response) return;
            else {
                //* 数据处理
                data.load = true;
                //* 接口请求
                switch (data.mode) {
                    case "RNA Splicer": {
                        return api.frontend("/api/ai/splicer", {
                            method: "POST",
                            data: {
                                data: [
                                    {
                                        ref: data.response.ref,
                                        alt: data.response.alt,
                                        name: data.response.name,
                                        gene_id: data.response.id,
                                        gene_pos: data.response.site,
                                        transcript_id: data.response.transcript,
                                    },
                                ],
                            },
                            finally: () => (data.load = false),
                            success: (response) => {
                                window.open(routerResolve({ name: "RnaSplicerResult", params: { id: response } }).href, "_blank");
                            },
                        });
                    }

                    case "Pathogenicity Predictor": {
                        return api.frontend("/api/ai/pathogenicity", {
                            data: {
                                data: [
                                    {
                                        gene_id: data.response.id,
                                        gene_pos: data.response.site,
                                        gene_name: data.response.name,
                                        reference: data.response.ref,
                                        alteration: data.response.alt,
                                        mutation_name: data.response.keyword,
                                        transcript_id: data.response.transcript,
                                    },
                                ],
                            },
                            method: "POST",
                            finally: () => (data.load = false),
                            success: (response) => {
                                window.open(routerResolve({ name: "PathogenicityResult", params: { id: response } }).href, "_blank");
                            },
                        });
                    }
                }
            }
        },
    };

    return { data, nodes, methods };
};
